import { DefaultValues } from 'react-hook-form';
import { z } from 'zod';

import { identitySchema } from '@/core/components';

import { currencyList } from '@feature/utils';

import {
  TravelExpense,
  TravelRequest,
  TravelRequestRequest,
  absenceMotivationList,
  absenceMotivationTakesLeave,
  erasmusTypeList,
  teachingCoverageModeList,
  travelActivityTypeList,
  travelExpenseKey,
  travelExpenseTypeList,
  travelFundingSourceList,
} from './TravelRequestTypes';

const travelRequestAbsenceSchema = z.object({
  teachingCoverageMode: z.enum(teachingCoverageModeList).nullable(),
  teachingCoverageNote: z.string().nullable(),
  absenceMotivation: z.enum(absenceMotivationList).nullable(),
  vacationDays: z.number().int().min(0).nullable(),
  unpaidDays: z.number().int().min(0).nullable(),
  ownProgramsMention: z.string().nullable(),
  substitutePersonName: z.string().nullable(),
});

const travelRequestFundingSchema = z.object({
  perDiem: z.boolean(),
  fundingSourceNote: z.string().nullable(),
  estimatedAmount: z.number().nullable(),
  estimatedAmountCurrency: z.enum(currencyList).nullable(),
});

export const travelExpenseSchema = z.object({
  expenseType: z.enum(travelExpenseTypeList),
  fundingSource: z.enum(travelFundingSourceList),
  requestedAmount: z.number().min(0).nullable(),
  currency: z.enum(currencyList),
  // Not .min(0): a field-level rule would fire on a hidden leftover, so the refine below gates it on what the row shows.
  eurAmount: z.number().nullable(),
  projectNumber: z.string().nullable(),
  projectTitle: z.string().nullable(),
  projectAmount: z.number().nullable(),
  projectEurAmount: z.number().nullable(),
});

const isBlank = (value: string | null) => value == null || value.trim() === '';

// Every field the form marks with an asterisk, under the same activity-type, motivation and coverage-mode conditions the form shows it.
export const travelRequestSchema = z
  .object({
    activityType: z.enum(travelActivityTypeList),
    erasmusType: z.enum(erasmusTypeList).nullable(),

    name: z.string().nullable(),
    destinationInstitution: z.string().nullable(),
    city: z.string().nullable(),
    country: identitySchema
      .extend({
        name: z.string(),
      })
      .nullable(),

    departureDate: z.string().nullable(),
    returnDate: z.string().nullable(),
    eventStartDate: z.string().nullable(),
    eventEndDate: z.string().nullable(),

    presentsPaper: z.boolean().nullable(),
    paperTitle: z.string().nullable(),

    absence: travelRequestAbsenceSchema,
    funding: travelRequestFundingSchema,

    expenses: z.array(travelExpenseSchema),
  })
  .superRefine((values, ctx) => {
    const required = (...path: (string | number)[]) =>
      ctx.addIssue({ code: z.ZodIssueCode.custom, path, params: { i18n: 'custom.required' } });
    const { activityType, absence } = values;
    const isErasmus = activityType === 'ERASMUS';

    if (isErasmus) {
      if (values.erasmusType == null) required('erasmusType');
    } else if (isBlank(values.name)) {
      required('name');
    }
    if (isBlank(values.destinationInstitution)) required('destinationInstitution');
    if (isBlank(values.city)) required('city');
    if (values.country == null) required('country');
    if (values.departureDate == null) required('departureDate');
    if (values.returnDate == null) required('returnDate');
    if (values.departureDate != null && values.returnDate != null && values.returnDate < values.departureDate) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['returnDate'],
        params: { i18n: 'custom.returnBeforeDeparture' },
      });
    }
    const { eventStartDate, eventEndDate } = values;
    const outsideTrip = (field: 'eventStartDate' | 'eventEndDate') =>
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: [field], params: { i18n: 'custom.eventOutsideTrip' } });
    if (eventStartDate == null) required('eventStartDate');
    if (eventEndDate == null) required('eventEndDate');
    if (eventStartDate != null && eventEndDate != null && eventEndDate < eventStartDate) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['eventEndDate'],
        params: { i18n: 'custom.eventEndBeforeStart' },
      });
    }
    if (eventStartDate != null && values.departureDate != null && eventStartDate < values.departureDate) {
      outsideTrip('eventStartDate');
    }
    if (eventEndDate != null && values.returnDate != null && eventEndDate > values.returnDate) {
      outsideTrip('eventEndDate');
    }
    if (activityType === 'SCIENTIFIC_EVENT' && values.presentsPaper === true && isBlank(values.paperTitle)) {
      required('paperTitle');
    }

    if (absence.teachingCoverageMode == null) required('absence', 'teachingCoverageMode');
    if (absence.teachingCoverageMode === 'COLLEAGUE_SUBSTITUTION' && isBlank(absence.substitutePersonName)) {
      required('absence', 'substitutePersonName');
    }
    if (!isErasmus) {
      if (absence.absenceMotivation == null) required('absence', 'absenceMotivation');
      if (absence.absenceMotivation === 'OWN_PROGRAMS' && isBlank(absence.ownProgramsMention)) {
        required('absence', 'ownProgramsMention');
      }
    }

    values.expenses.forEach((expense, index) => {
      // The mobility support line is a yes/no tick that carries no amount.
      if (expense.expenseType === 'UMFST_MOBILITY_SUPPORT') return;
      if (expense.requestedAmount == null) required('expenses', index, 'requestedAmount');
      if (expense.currency === 'RON') {
        if (expense.eurAmount == null || expense.eurAmount < 0) required('expenses', index, 'eurAmount');
        // Project amounts survive a funding-source switch that unmounts their inputs, so the rule tracks the row's source.
        if (
          expense.fundingSource === 'PROJECT_FUNDS' &&
          expense.projectAmount != null &&
          (expense.projectEurAmount == null || expense.projectEurAmount < 0)
        ) {
          required('expenses', index, 'projectEurAmount');
        }
      }
    });

    const expenseKeys = values.expenses.map(travelExpenseKey);
    expenseKeys.forEach((expenseKey, index) => {
      if (expenseKeys.indexOf(expenseKey) !== expenseKeys.lastIndexOf(expenseKey)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['expenses', index, 'fundingSource'],
          params: { i18n: 'custom.expenseSourceRepeated' },
        });
      }
    });
  });

export type TravelRequestSchema = z.infer<typeof travelRequestSchema>;
export type TravelExpenseSchema = z.infer<typeof travelExpenseSchema>;

type TravelRequestAbsenceSchema = z.infer<typeof travelRequestAbsenceSchema>;
type TravelRequestFundingSchema = z.infer<typeof travelRequestFundingSchema>;

// Text inputs are controlled, so a null string from the API becomes '' in the form and '' becomes null on the wire.
const text = (value: string | null | undefined): string => value ?? '';
const blank = (value: string | null): string | null => (value === '' ? null : value);

function toAbsenceSchema(absence: TravelRequest['absence']): TravelRequestAbsenceSchema {
  return {
    teachingCoverageMode: absence?.teachingCoverageMode ?? null,
    teachingCoverageNote: text(absence?.teachingCoverageNote),
    absenceMotivation: absence?.absenceMotivation ?? null,
    vacationDays: absence?.vacationDays ?? null,
    unpaidDays: absence?.unpaidDays ?? null,
    ownProgramsMention: text(absence?.ownProgramsMention),
    substitutePersonName: text(absence?.substitutePersonName),
  };
}

function toFundingSchema(funding: TravelRequest['funding']): TravelRequestFundingSchema {
  return {
    perDiem: funding?.perDiem ?? false,
    fundingSourceNote: text(funding?.fundingSourceNote),
    estimatedAmount: funding?.estimatedAmount ?? null,
    // The form's currency field is locked to EUR, so it opens on EUR rather than empty.
    estimatedAmountCurrency: funding?.estimatedAmountCurrency ?? 'EUR',
  };
}

export const defaultTravelRequestValues: DefaultValues<TravelRequestSchema> = {
  activityType: 'SCIENTIFIC_EVENT',
  erasmusType: null,
  name: '',
  destinationInstitution: '',
  city: '',
  country: null,
  departureDate: null,
  returnDate: null,
  eventStartDate: null,
  eventEndDate: null,
  presentsPaper: false,
  paperTitle: '',
  absence: toAbsenceSchema(null),
  funding: toFundingSchema(null),
  expenses: [],
};

export const defaultTravelExpenseValues: TravelExpenseSchema = {
  expenseType: 'TRANSPORT',
  fundingSource: 'UMFST',
  requestedAmount: null,
  currency: 'RON',
  eurAmount: null,
  projectNumber: '',
  projectTitle: '',
  projectAmount: null,
  projectEurAmount: null,
};

function toTravelExpenseSchema(expense: TravelExpense): TravelExpenseSchema {
  return {
    expenseType: expense.expenseType,
    fundingSource: expense.fundingSource,
    requestedAmount: expense.requestedAmount,
    currency: expense.currency,
    eurAmount: expense.eurAmount,
    projectNumber: text(expense.projectNumber),
    projectTitle: text(expense.projectTitle),
    projectAmount: expense.projectAmount,
    projectEurAmount: expense.projectEurAmount,
  };
}

export function toTravelRequestSchema(request: TravelRequest): TravelRequestSchema {
  return {
    activityType: request.activityType,
    erasmusType: request.erasmusType,
    name: text(request.name),
    destinationInstitution: text(request.destinationInstitution),
    city: text(request.city),
    country: request.country == null ? null : { id: request.country.id, name: request.country.name },
    departureDate: request.departureDate,
    returnDate: request.returnDate,
    eventStartDate: request.eventStartDate ?? null,
    eventEndDate: request.eventEndDate ?? null,
    presentsPaper: request.presentsPaper ?? false,
    paperTitle: text(request.paperTitle),
    absence: toAbsenceSchema(request.absence),
    funding: toFundingSchema(request.funding),
    expenses: request.expenses.map(toTravelExpenseSchema),
  };
}

export function toTravelRequestRequest(values: TravelRequestSchema): TravelRequestRequest {
  const takesLeave = values.activityType !== 'ERASMUS' && absenceMotivationTakesLeave(values.absence.absenceMotivation);

  return {
    activityType: values.activityType,
    erasmusType: values.erasmusType,
    name: blank(values.name),
    destinationInstitution: blank(values.destinationInstitution),
    city: blank(values.city),
    country: values.country == null ? null : { id: values.country.id },
    departureDate: values.departureDate,
    returnDate: values.returnDate,
    eventStartDate: values.eventStartDate,
    eventEndDate: values.eventEndDate,
    presentsPaper: values.presentsPaper,
    paperTitle: blank(values.paperTitle),
    absence: {
      ...values.absence,
      // The leave inputs unmount with the motivation, so whatever they kept is dropped under a motivation that takes no leave.
      vacationDays: takesLeave ? values.absence.vacationDays : null,
      unpaidDays: takesLeave ? values.absence.unpaidDays : null,
      teachingCoverageNote: blank(values.absence.teachingCoverageNote),
      ownProgramsMention: blank(values.absence.ownProgramsMention),
      // The name input unmounts with the coverage mode, so a name kept from another mode is dropped.
      substitutePersonName:
        values.absence.teachingCoverageMode === 'COLLEAGUE_SUBSTITUTION'
          ? blank(values.absence.substitutePersonName)
          : null,
    },
    funding: {
      ...values.funding,
      fundingSourceNote: blank(values.funding.fundingSourceNote),
      estimatedAmountCurrency: values.funding.estimatedAmount == null ? null : 'EUR',
    },
    expenses: values.expenses.map(expense => {
      if (expense.expenseType === 'UMFST_MOBILITY_SUPPORT') {
        return {
          ...defaultTravelExpenseValues,
          expenseType: expense.expenseType,
          projectNumber: null,
          projectTitle: null,
        };
      }
      // The hidden inputs keep their values, so every field the row no longer shows is dropped here rather than sent.
      const onProject = expense.fundingSource === 'PROJECT_FUNDS';
      const inRon = expense.currency === 'RON';
      return {
        ...expense,
        projectNumber: onProject ? blank(expense.projectNumber) : null,
        projectTitle: onProject ? blank(expense.projectTitle) : null,
        projectAmount: onProject ? expense.projectAmount : null,
        eurAmount: inRon ? expense.eurAmount : null,
        projectEurAmount: onProject && inRon ? expense.projectEurAmount : null,
      };
    }),
  };
}
